// Категория: Vue-миксин, состояние живёт только на инстансе компонента.
//
// Оба компонента выдачи (MainResults.vue и PlaylistCard.vue) решают одну и ту же
// задачу: запрос форматов можно начать заново, пока предыдущий ещё летит, а инстанс
// компонента может достаться другому видео. Значит, у каждого асинхронного
// продолжения нужно спросить «а моя попытка ещё актуальна?» — иначе ответ по старой
// ссылке перезапишет уже показанную выдачу.
//
// Раньше этот guard был скопирован в оба компонента (свой AbortController, свой
// счётчик, своя сверка) и успел разойтись в деталях: один проверял актуальность в
// .finally(), другой не проверял вовсе. Здесь он один.
//
// Что НЕ входит в миксин: критерий «то же самое видео». У карточки это source,
// у одиночной выдачи — link, общей формы у этого нет. Вызывающий передаёт готовый
// ответ признаком `stale`, а миксин владеет всем остальным.

// из текстового модуля, а не из downloadActions: там window.LocalConverter и DOM,
// и миксину с состоянием инстанса тянуть их ради пары строк незачем
import { buildFailurePayload } from '../tools/downloadMessages';

export default {
    data() {
        return {
            // Идёт ли скачивание. Пока идёт, кнопку держим заблокированной: повторный
            // клик запустил бы параллельный window.LocalConverter.runConvert() или
            // второе окно конвертера на то же видео.
            isDownloading: false,
            // Номер попытки скачивания. Ссылка/видео могут не смениться (то же видео,
            // другое качество), и тогда устаревшую попытку отличает только номер.
            downloadAttempt: 0,
            // Контроллер текущего запроса. Объявлен здесь, а не заводится на лету:
            // иначе это скрытый контракт, и объявление такого же поля в data()
            // компонента разошлось бы с миксином молча, без предупреждения Vue.
            requestController: null,
        };
    },
    beforeDestroy() {
        // хук миксина выполняется вместе с хуком компонента, а не вместо него
        this.abortRequest();
    },
    methods: {
        /**
         * Начинает новую попытку запроса, отменяя предыдущую.
         * Возвращённый контроллер вызывающий сохраняет в локальную переменную и
         * передаёт в isCurrentRequest() после каждого await — держать его только
         * в this недостаточно, там уже лежит контроллер следующей попытки.
         * @returns {AbortController}
         */
        beginRequest() {
            this.abortRequest();
            this.requestController = new AbortController();
            return this.requestController;
        },
        abortRequest() {
            if (!this.requestController) {
                return;
            }
            this.requestController.abort();
            // Сбросить обязательно, а не только abort(): если промис успел
            // разрешиться ДО отмены, его продолжение уже стоит в очереди микротасок
            // и AbortError не бросит. Единственное, что его отличает, — потеря
            // идентичности контроллера, поэтому isCurrentRequest() должен вернуть
            // false сразу после отмены, а не только после старта следующей попытки.
            this.requestController = null;
        },
        /**
         * Актуальна ли ещё эта попытка.
         *
         * Одной проверки isAbortError мало: abort() отклоняет промис микротаской,
         * и продолжение уже осевшего await успевает выполниться с данными прошлой
         * ссылки. Поэтому спрашиваем перед каждой записью в состояние и перед
         * отправкой события в аналитику.
         * @param controller {AbortController} - контроллер своей попытки
         * @returns {boolean}
         */
        isCurrentRequest(controller) {
            return this.requestController === controller;
        },
        /**
         * Сбрасывает состояние скачивания под новое видео или новую ссылку.
         *
         * Инкремент номера обесценивает незавершённую попытку: её onSettled уже не
         * снимет блокировку с новой, а её поздний отказ не покажется поверх неё.
         * Без этого пользователь, скачавший одно видео и сразу отправивший другую
         * ссылку, получал бы мёртвую кнопку Download на новой выдаче.
         */
        resetDownloadState() {
            this.downloadAttempt += 1;
            this.isDownloading = false;
        },
        /**
         * Помечает начало скачивания.
         *
         * finish() идемпотентна и привязана к своей попытке: поздний вызов от
         * прошлого скачивания не должен разблокировать текущее.
         * @returns {{attempt: number, finish: Function}}
         */
        beginDownload() {
            this.downloadAttempt += 1;
            const attempt = this.downloadAttempt;
            this.isDownloading = true;

            return {
                attempt,
                finish: () => {
                    if (attempt === this.downloadAttempt) {
                        this.isDownloading = false;
                    }
                },
            };
        },
        /**
         * Логирует исключение, которое не похоже на сетевую ошибку.
         *
         * В catch запроса долетает и баг разбора успешного ответа: у такого Error нет
         * ни response, ни code, buildConvertErrorResult вернёт на него null, и в GA
         * он станет безликим 'fatal', а в UI — общим «Something went wrong». Без лога
         * клиентский баг неотличим от отказа сети.
         * @param e {Error}
         * @param scope {string} - префикс лога, например 'main-results'
         */
        logUnexpectedFailure(e, scope) {
            if (!e.response && !e.code) {
                console.error(`[${scope}] unexpected failure while handling the response`, e);
            }
        },
        /**
         * Общая часть отчёта об отказе скачивания.
         *
         * Компонент оставляет себе только то, что у него своё: признак устаревания
         * (карточка сверяет source, одиночная выдача — link) и место показа
         * сообщения. Пара «текст + result для аналитики» строится здесь, чтобы они
         * не разошлись между двумя компонентами — расхождение такого рода уже
         * приводило к потерянной обработке isLocalConverter.
         * @param reason {string} - значение NO_FORMATS_REASON
         * @param options {Object}
         * @param options.attempt {number} - номер попытки, за которую отчитываемся
         * @param options.stale {boolean} - видео/ссылка уже другие
         * @param options.suffix {string} - уточнение к тексту
         * @param options.apply {Function} - (message, result) показать и отправить
         */
        reportDownloadFailure(reason, { attempt, stale, suffix, apply }) {
            if (stale || attempt !== this.downloadAttempt) {
                return;
            }
            const { message, result } = buildFailurePayload(reason, suffix);
            apply(message, result);
        },
    },
};
